import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Grid, Paper, Avatar
} from '@mui/material';
import {
  ExitToApp as ExitIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
  Add as AddIcon // 如果之前没有
} from '@mui/icons-material';
import '../styles/CourseManagement.css';
import { EDUGATE_API_URL } from '../config/serviceConfig';

const UserCoursesPanel = ({ 
  currentUser, 
  showNotification, 
  onCourseChange,
  onJoinClick,
  accentColor = '#4D8BFF'
}) => {
  const { t } = useTranslation();
  const accent = accentColor || '#4D8BFF';
  // CAS用户获取student_id的工具函数
  const getCasUserStudentId = async (employeeNumber, userName, userType = 'bupt_cas', phone = '') => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-Cas-User-ID': encodeURIComponent(employeeNumber),
        'X-User-Name': encodeURIComponent(userName),
        'X-User-Type': userType // ✅ 添加用户类型
      };
      
      const response = await fetch(`${EDUGATE_API_URL}/api/auth/get-student-id`, {
        method: 'POST',
        headers: headers
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.code === 0) {
          return result.data.student_id;
        }
      }
      return null;
    } catch (error) {
      console.error('获取CAS用户student_id失败:', error);
      return null;
    }
  };

  // 获取或缓存用户的student_id
  const getUserStudentId = async () => {
    console.log('getUserStudentId调用 - currentUser:', currentUser);
    
    // 如果已经有student_id，直接返回
    if (currentUser.student_id) {
      console.log('使用现有student_id:', currentUser.student_id);
      return currentUser.student_id;
    }
    
    // 如果是CAS用户且有employeeNumber，获取student_id
    if (currentUser.isCasUser && currentUser.employeeNumber) {
      console.log('CAS用户，正在获取student_id...', currentUser.employeeNumber, currentUser.name);
      const studentId = await getCasUserStudentId(
        currentUser.employeeNumber, 
        currentUser.name,
        currentUser.user_type || 'bupt_cas'
      );
      console.log('获取到的student_id:', studentId);
      if (studentId) {
        // 缓存到currentUser对象中，避免重复请求
        currentUser.student_id = studentId;
        console.log('student_id已缓存到currentUser');
        return studentId;
      }
    } else {
      console.log('非CAS用户或缺少employeeNumber:', {
        isCasUser: currentUser.isCasUser,
        employeeNumber: currentUser.employeeNumber
      });
    }
    
    // 其他情况返回空字符串
    console.log('返回空student_id');
    return '';
  };

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [leaveLoading, setLeaveLoading] = useState(false);

  // 获取用户课程列表
  const fetchUserCourses = async () => {
    if (!currentUser?.username) return;

    // 获取student_id（懒加载方式）
    const studentId = await getUserStudentId();
    if (!studentId) {
      showNotification('身份验证失败，请重新登录', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${EDUGATE_API_URL}/api/courses/user-enrollments?student_id=${studentId}`
      ); 
      const data = await response.json();

      if (data.code === 0) {
        setEnrollments(data.data.enrollments || []);
      } else {
        showNotification(data.message || '获取课程列表失败', 'error');
      }
    } catch (error) {
      console.error('获取课程列表错误:', error);
      showNotification('获取课程列表失败，请稍后重试', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    fetchUserCourses();
  }, [currentUser]);

  // 打开退出确认对话框
  const handleOpenLeaveDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setLeaveDialogOpen(true);
  };

  // 关闭退出确认对话框
  const handleCloseLeaveDialog = () => {
    setLeaveDialogOpen(false);
    setSelectedEnrollment(null);
  };

  // 确认退出课程
  const handleConfirmLeave = async () => {
    if (!selectedEnrollment) return;

    // 获取student_id
    const studentId = await getUserStudentId();
    if (!studentId) {
      showNotification('身份验证失败，请重新登录', 'error');
      return;
    }

    setLeaveLoading(true);
    try {
      const response = await fetch(
        `${EDUGATE_API_URL}/api/courses/${selectedEnrollment.course_id}/classes/${selectedEnrollment.class_id}/leave?student_id=${studentId}`,
        {
          method: 'DELETE'
        }
      );
      const data = await response.json();

      if (data.code === 0) {
        showNotification('成功退出课程班级', 'success');
        handleCloseLeaveDialog();
        fetchUserCourses(); // 刷新列表
        onCourseChange && onCourseChange(); // 通知父组件
      } else {
        showNotification(data.message || '退出失败', 'error');
      }
    } catch (error) {
      console.error('退出课程错误:', error);
      showNotification('退出失败，请稍后重试', 'error');
    } finally {
      setLeaveLoading(false);
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: '#c3c9d0', fontWeight: 600 }}>
          {t('user_courses_joined', { count: enrollments.length })}
        </Typography>
        <IconButton 
          onClick={fetchUserCourses} 
          disabled={loading}
          sx={{ 
            color: '#a6afb8',
            borderRadius: '6px',
            '&:hover': { color: '#fff', background: '#303238' }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={36} sx={{ color: accent }} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {/* 加入课程卡片 */}
          <Grid item xs={12} sm={6}>
            <Paper
              onClick={onJoinClick}
              sx={{
                height: '100%',
                minHeight: '136px',
                borderRadius: '6px',
                background: '#252526',
                border: '1px dashed #59606a',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'background-color 0.16s ease, border-color 0.16s ease',
                '&:hover': {
                  background: '#292a2d',
                  borderColor: accent,
                  transform: 'none'
                }
              }}
            >
              <Box sx={{ 
                p: 1,
                borderRadius: '6px',
                background: `${accent}1a`,
                mb: 1
              }}>
                <AddIcon sx={{ fontSize: 24, color: accent }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#e7e7e7' }}>
                {t('user_courses_join_new')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                {t('user_courses_join_hint')}
              </Typography>
            </Paper>
          </Grid>

          {/* 课程列表卡片 */}
          {enrollments.map((enrollment) => (
            <Grid item xs={12} sm={6} key={enrollment.id}>
              <Paper sx={{
                p: 2,
                height: '100%',
                minHeight: '136px',
                borderRadius: '6px',
                background: '#292a2d',
                border: '1px solid #3e3e42',
                transition: 'background-color 0.16s ease, border-color 0.16s ease',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  background: '#303238',
                  transform: 'none',
                  boxShadow: 'none',
                  borderColor: accent,
                  '& .leave-btn': { opacity: 1 }
                }
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: accent, width: 36, height: 36, fontSize: '1rem' }}>
                      {enrollment.course_name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 650, color: '#e7e7e7', lineHeight: 1.2 }}>
                        {enrollment.course_name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 0.5 }}>
                        {enrollment.class_name}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    className="leave-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenLeaveDialog(enrollment);
                    }}
                    size="small"
                    sx={{ 
                      opacity: 0, 
                      transition: 'opacity 0.2s',
                      color: 'rgba(255, 255, 255, 0.3)',
                      '&:hover': { color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)' }
                    }}
                  >
                    <ExitIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255, 255, 255, 0.5)' }}>
                    <PersonIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption">
                      {enrollment.contact || '暂无教师信息'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255, 255, 255, 0.5)' }}>
                    <DateRangeIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption">
                      加入时间：{formatDate(enrollment.created_at)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* 退出确认对话框 */}
      <Dialog
        open={leaveDialogOpen}
        onClose={handleCloseLeaveDialog}
        maxWidth="xs"
        className="workbench-dialog course-leave-dialog"
        PaperProps={{
          className: 'workbench-dialog-paper',
          style: { '--workbench-accent': accent },
          sx: {
            borderRadius: '10px',
            background: '#1e1e1e',
            border: '1px solid #3e3e42',
            color: '#e7e7e7'
          }
        }}
      >
        <DialogTitle className="workbench-dialog-title" sx={{ borderTop: `2px solid ${accent}` }}>{t('user_courses_leave_title')}</DialogTitle>
        <DialogContent className="workbench-dialog-content">
          {selectedEnrollment && (
            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                {t('user_courses_leave_msg')}
              </Typography>
              <Paper sx={{ p: 2, background: '#292a2d', border: '1px solid #3e3e42', borderRadius: '6px' }}>
                <Typography variant="subtitle2" sx={{ color: accent }}>
                  {selectedEnrollment.course_name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  {selectedEnrollment.class_name}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseLeaveDialog} sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            {t('user_courses_leave_cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmLeave}
            disabled={leaveLoading}
            sx={{
              bgcolor: '#ef4444',
              '&:hover': { bgcolor: '#dc2626' }
            }}
          >
            {leaveLoading ? <CircularProgress size={20} color="inherit" /> : t('user_courses_leave_btn')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserCoursesPanel; 